import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import {RecentBooking, RecentMot, UserDealer, UserRegion, WeeklyUtilization} from "./models"
import {Vehicle} from "../Vehicles/vehicleSlice"
import {UpdateScheduleModel} from "../DVSASchedules/schedulesSlice"
import {appInsights} from "."
export type Permission = {
    roleId: string
    permissions?: []
}

export interface ErrorList {
    traceId: string | undefined
    message: string
    statusCode: string
}
type AppState = {
    gettingToken: boolean
    accessToken: string
    userInfo: any
    userRegions: UserRegion[]
    selectedRegion: UserRegion
    defaultRegionSelected: boolean
    userDealers: UserDealer[]
    selectedSingleDealer: UserDealer | null
    selectedMultipleDealer: UserDealer[] | null
    selectedNav: string
    searchedVehicle: Vehicle | null
    loadingVehicleDetails: boolean
    loadingRecentMOT: boolean
    recentMot: RecentMot[]
    loadingRecentBookings: boolean
    recentBookings: RecentBooking[]
    loadingWeeklyUtilization: boolean
    weeklyUtilizationGraph: WeeklyUtilization[]
    userAllRegionsAndDealers: UserDealer[]
    loadingSchedulesForMOT: boolean
    schedulesForMOT: UpdateScheduleModel[]
    loadingSchedulesForDealer: boolean
    schedulesForDealer: UpdateScheduleModel[]
    permissions: Permission | null
    toastr: {isActive: boolean; message: string; delay: number; title: string; type: string; description: string | null}
    errorList: ErrorList[]
}

const initialState: AppState = {
    gettingToken: false,
    accessToken: "",
    userInfo: null,
    userRegions: [],
    selectedRegion: {} as UserRegion,
    defaultRegionSelected: false,
    userDealers: [],
    selectedSingleDealer: null,
    selectedMultipleDealer: null,
    selectedNav: "",
    searchedVehicle: null,
    loadingVehicleDetails: false,
    loadingRecentMOT: false,
    recentMot: [],
    loadingRecentBookings: false,
    recentBookings: [],
    loadingWeeklyUtilization: false,
    weeklyUtilizationGraph: [],
    userAllRegionsAndDealers: [],
    loadingSchedulesForMOT: false,
    schedulesForMOT: [],
    loadingSchedulesForDealer: false,
    schedulesForDealer: [],
    permissions: null,
    toastr: {isActive: false, message: "", delay: 0, title: "", type: "", description: ""},
    errorList: [],
}

const appSlice = createSlice({
    name: "app",
    initialState,
    reducers: {
        setSelectedNav(state, action: PayloadAction<string>) {
            state.selectedNav = action.payload
        },
        loadingAccessToken(state, action: PayloadAction<boolean>) {
            state.gettingToken = action.payload
        },
        setAccessToken(state, action: PayloadAction<string>) {
            state.accessToken = action.payload
        },

        setUserInformationData(state, action: PayloadAction<any>) {
            state.userInfo = action.payload
        },
        setUserRegionsData(state, action: PayloadAction<any>) {
            state.userRegions = action.payload
        },
        setSelectedRegion(state, action: PayloadAction<[UserRegion, boolean]>) {
            state.selectedRegion = action.payload[0]
            state.defaultRegionSelected = action.payload[1]
        },

        setUserDealersData(state, action: PayloadAction<UserDealer[]>) {
            state.userDealers = action.payload
        },
        setSelectedSingleDealer(state, action: PayloadAction<UserDealer | null>) {
            state.selectedSingleDealer = action.payload
        },
        setSelectedMultipleDealer(state, action: PayloadAction<UserDealer[] | null>) {
            state.selectedMultipleDealer = action.payload
        },
        setSearchedVehicleDetails(state, action: PayloadAction<[boolean, Vehicle | null]>) {
            state.loadingVehicleDetails = action.payload[0]
            state.searchedVehicle = action.payload[1]
        },
        setRecentMOT(state, action: PayloadAction<[boolean, RecentMot[]]>) {
            state.loadingRecentMOT = action.payload[0]
            state.recentMot = action.payload[1]
        },
        setRecentBookings(state, action: PayloadAction<[boolean, RecentBooking[]]>) {
            state.loadingRecentBookings = action.payload[0]
            state.recentBookings = action.payload[1]
        },
        setUtilizationLoader(state, action: PayloadAction<boolean>) {
            state.loadingWeeklyUtilization = action.payload
        },
        setWeeklyUtilization(state, action: PayloadAction<WeeklyUtilization[]>) {
            state.weeklyUtilizationGraph = action.payload
        },

        setResetVehicleSearchForm(state) {
            state.searchedVehicle = null
            state.recentMot = []
            state.recentBookings = []
        },
        setUserAllRegionsAndDealers(state, action: PayloadAction<UserDealer[]>) {
            state.userAllRegionsAndDealers = action.payload
        },
        setLoadingSchedulesForMOT(state, action: PayloadAction<boolean>) {
            state.loadingSchedulesForMOT = action.payload
        },
        setSchedulesForMOT(state, action: PayloadAction<UpdateScheduleModel[]>) {
            state.schedulesForMOT = action.payload
        },
        setLoadingSchedulesForDealer(state, action: PayloadAction<boolean>) {
            state.loadingSchedulesForDealer = action.payload
        },
        setSchedulesForDealer(state, action: PayloadAction<UpdateScheduleModel[]>) {
            state.schedulesForDealer = action.payload
        },
        setAccessPermissions(state, action: PayloadAction<Permission>) {
            state.permissions = action.payload
        },
        setToastr(
            state,
            action: PayloadAction<{
                isActive: boolean
                message: string
                delay: number
                title: string
                type: string
                description: string | null
            }>,
        ) {
            state.toastr = action.payload
        },
        setErrorList(state, action: PayloadAction<ErrorList>) {
            state.errorList.push(action.payload)
        },
    },
})

export const {
    setSelectedNav,
    loadingAccessToken,
    setAccessToken,
    setUserInformationData,
    setUserRegionsData,
    setSelectedRegion,
    setUserDealersData,
    setSelectedSingleDealer,
    setSelectedMultipleDealer,
    setSearchedVehicleDetails,
    setRecentMOT,
    setRecentBookings,
    setResetVehicleSearchForm,
    setUtilizationLoader,
    setWeeklyUtilization,
    setUserAllRegionsAndDealers,
    setLoadingSchedulesForMOT,
    setSchedulesForMOT,
    setLoadingSchedulesForDealer,
    setSchedulesForDealer,
    setAccessPermissions,
    setToastr,
    setErrorList,
} = appSlice.actions

export default appSlice.reducer

export const setAccessTokenAction =
    (token: string): AppThunk =>
    dispatch => {
        dispatch(setAccessToken(token))
    }

export const loadingAccessTokenAction =
    (data: boolean): AppThunk =>
    dispatch => {
        dispatch(loadingAccessToken(data))
    }

export const setUserInformationDataAction =
    (data: any): AppThunk =>
    dispatch => {
        dispatch(setUserInformationData(data))
    }

export const setSelectedNavAction =
    (data: string): AppThunk =>
    dispatch => {
        dispatch(setSelectedNav(data))
    }

export const getUserAllRegionsAndDealers =
    (userId: string): AppThunk =>
    (dispatch: any, getState) => {
        httpService(getAccessToken(getState()))
            .get(`users/${userId}/dealers`)
            .then(res => {
                dispatch(setUserAllRegionsAndDealers(res.data.filter((t: UserDealer) => t.active === true)))
            })
            .catch(error => {
                dispatch(
                    setErrorList({
                        traceId: appInsights.context.telemetryTrace.traceID,
                        message: "Unable to fetch dealers",
                        statusCode: error.response && error.response.status,
                    }),
                )
                dispatch(setUserAllRegionsAndDealers([]))
            })
    }

export const getUserRegionsAction = (): AppThunk => (dispatch, getState) => {
    const userId = getState().application.userInfo.idTokenClaims.oid
    httpService(getAccessToken(getState()))
        .get(`users/${userId}/regions`)
        .then(res => {
            if (res.data.length <= 0) throw new Error("No dealers assigned")
            if (typeof res.data === 'object' && res.data.hasOwnProperty('value')) throw new Error(res.data.value.error)

            const userRegions: UserRegion[] = res.data.filter(onlyActiveRegions).map((x: UserRegion) => {
                return {regionId: x.regionId, regionName: x.regionName}
            })
            httpService(getAccessToken(getState()))
                .get(`${userId}/default/REGION`)
                .then(res1 => {
                    const selectedRegion =
                        res1.data !== ""
                            ? userRegions.find(x => x.regionId === parseInt(res1.data)) ?? userRegions[0]
                            : userRegions[0]
                    dispatch(setUserRegionsData(userRegions))

                    if (selectedRegion !== undefined) {
                        dispatch(setSelectedRegion([selectedRegion, true]))
                    }
                })
        })
        .catch(error => {
            appInsights.trackException({
                exception: error,
                properties: {
                    Request: error.request,
                    Response: error.response,
                    UserId: userId,
                    message: error.response ? "Unable to fetch regions" : error,
                },
            })

            dispatch(
                setErrorList({
                    traceId: appInsights.context.telemetryTrace.traceID,
                    message: error.response ? "Unable to fetch regions" : error,
                    statusCode: error.response && error.response.status,
                }),
            )
            dispatch(setUserRegionsData([]))
        })
}

export const setUserRegionsAction =
    (regionId: number): AppThunk =>
    (dispatch, getState) => {
        const userId = getState().application.userInfo.idTokenClaims.oid
        httpService(getAccessToken(getState()))
            .post(`${userId}/default/REGION`, {regionId: regionId})
            .then(res => {
                // eslint-disable-next-line no-restricted-globals
                location.reload()
            })
    }

const onlyActiveRegions = (region: UserRegion) => {
    return region.active === true
}

export const getUserDealersAction =
    (type: string): AppThunk =>
    (dispatch, getState) => {
        /* TYPE VALUE :  SINGLE_DEALER or MULTIPLE_DEALER*/
        const userId = getState().application.userInfo.idTokenClaims.oid
        const regionId = getState().application?.selectedRegion?.regionId
        httpService(getAccessToken(getState()))
            .get(`users/${userId}/regions/${regionId}/dealers`)
            .then(res => {
                const userDealers: UserDealer[] = res.data.filter(onlyActiveDealers).map((x: UserDealer) => {
                    return {
                        dealerId: x.dealerId,
                        dealerName: x.dealerName,
                        dealerNumber: x.dealerNumber,
                        preparerId: x.preparerId,
                        hasLocalATF: x.hasLocalATF,
                    }
                })
                dispatch(setUserDealersData(userDealers))
                httpService(getAccessToken(getState()))
                    .get(`${userId}/default/${type}`)
                    .then(res1 => {
                        if (type === "SINGLE_DEALER") {
                            const dealerSelection =
                                res1.data !== "" ? userDealers.find(x => x.dealerId === parseInt(res1.data)) : null

                            dispatch(
                                setSelectedSingleDealer(
                                    dealerSelection !== undefined ? dealerSelection : userDealers[0],
                                ),
                            )
                        }
                        if (type === "MULTIPLE_DEALER") {
                            if (res1.data !== "") {
                                const dealers = res1.data.toString().split(",").map(Number)
                                const selectedDealers = userDealers.filter(value => dealers.includes(value.dealerId))
                                const dealerSelection = selectedDealers ?? null
                                if (dealerSelection !== null) {
                                    dispatch(setSelectedMultipleDealer(dealerSelection))
                                }
                            }
                        }
                    })
            })
            .catch(error => {
                appInsights.trackException({
                    exception: error,
                    properties: {
                        Request: error.request,
                        Response: error.response,
                        message: "Unable to fetch your dealers",
                    },
                })
                dispatch(
                    setErrorList({
                        traceId: appInsights.context.telemetryTrace.traceID,
                        message: "Unable to fetch your dealers",
                        statusCode: error.response && error.response.status,
                    }),
                )
                dispatch(setUserRegionsData([]))
            })
    }

const onlyActiveDealers = (region: UserRegion) => {
    return region.active === true
}

export const setUserDefaultDealers =
    (type: string, dealerId: number = 0, dealerIds: number[] = []): AppThunk =>
    (dispatch, getState) => {
        const regionId = getState().application?.selectedRegion?.regionId

        /* TYPE VALUE :  SINGLE_DEALER or MULTIPLE_DEALER*/
        const userId = getState().application.userInfo.idTokenClaims.oid
        const userDealers = getState().application?.userDealers

        if (type === "SINGLE_DEALER") {
            const selectedDealer = userDealers.find(x => x.dealerId === dealerId)

            dispatch(setSelectedSingleDealer(selectedDealer ?? null))
        }
        if (type === "MULTIPLE_DEALER") {
            const selectedDealers = userDealers.filter(value => dealerIds.includes(value.dealerId))
            dispatch(setSelectedMultipleDealer(selectedDealers ?? null))
        }

        httpService(getAccessToken(getState()))
            .post(`${userId}/default/${type}`, {regionId: regionId, dealerId: dealerId, dealerIds: dealerIds})
            .then(res => {})
    }

export const fetchSearchedVehicleDetail =
    (registrationNumber: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setSearchedVehicleDetails([true, null]))
        dispatch(setRecentMOT([false, []]))
        dispatch(setRecentBookings([false, []]))

        httpService(getAccessToken(getState()))
            .get(`vehicles/annual-tests/${registrationNumber}`)
            .then(res => {
                const vehicleDetail = res.data as Vehicle
                dispatch(
                    setSearchedVehicleDetails([
                        false,
                        {...vehicleDetail, registration: vehicleDetail.registration ?? registrationNumber},
                    ]),
                )
                dispatch(fetchRecentAnnualTests(vehicleDetail.registration))
                dispatch(fetchRecentBookings(vehicleDetail.registration))
            })
            .catch(error => dispatch(setSearchedVehicleDetails([false, null])))
    }

export const fetchRecentAnnualTests =
    (registrationNumber: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRecentMOT([true, []]))
        httpService(getAccessToken(getState()))
            .get(`vehicles/recent-annual-tests/${registrationNumber}`)
            .then(res => {
                const recentTests = res.data as RecentMot[]
                dispatch(setRecentMOT([false, recentTests]))
            })
            .catch(error => dispatch(setRecentMOT([false, []])))
    }

export const refreshRecentAnnualTests =
    (registrationNumber: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRecentMOT([true, []]))
        httpService(getAccessToken(getState()))
            .get(`vehicles/search/${registrationNumber}`)
            .then(res => {
                dispatch(fetchRecentAnnualTests(registrationNumber))
            })
            .catch(error => dispatch(setRecentMOT([false, []])))
    }

export const fetchRecentBookings =
    (registrationNumber: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRecentBookings([true, []]))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/recent-bookings/${registrationNumber}`)
            .then(res => {
                const recentBookings = res.data as RecentBooking[]
                dispatch(setRecentBookings([false, recentBookings]))
            })
            .catch(error => dispatch(setRecentBookings([false, []])))
    }

export const fetchWeeklyUtilization =
    (dealerId: number, startDate: string, pageNumber: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setWeeklyUtilization([]))
        dispatch(setUtilizationLoader(true))
        httpService(getAccessToken(getState()))
            .get(
                `regions/${getSelectedRegionId(
                    getState(),
                )}/weekly-utilization?dealerId=${dealerId}&&startDate=${startDate}&&page=${pageNumber}`,
            )
            .then(res => {
                const data = res.data as WeeklyUtilization[]
                dispatch(setWeeklyUtilization(data))
                dispatch(setUtilizationLoader(false))
            })
            .catch(error => {
                dispatch(setWeeklyUtilization([]))
                dispatch(setUtilizationLoader(false))
            })
    }

export const fetchSchedulesForValidation =
    (dealerId: string, forBooking: boolean = false): AppThunk =>
    (dispatch: any, getState) => {
        forBooking ? dispatch(setLoadingSchedulesForMOT(true)) : dispatch(setLoadingSchedulesForDealer(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/dealers/${dealerId}/schedules`)
            .then(res => {
                forBooking ? dispatch(setSchedulesForMOT(res.data)) : dispatch(setSchedulesForDealer(res.data))
                forBooking ? dispatch(setLoadingSchedulesForMOT(false)) : dispatch(setLoadingSchedulesForDealer(false))
            })
            .catch(error => {
                forBooking ? dispatch(setSchedulesForMOT([])) : dispatch(setSchedulesForDealer([]))
                forBooking ? dispatch(setLoadingSchedulesForMOT(false)) : dispatch(setLoadingSchedulesForDealer(false))
            })
    }

export const getAccessPermission = (): AppThunk => (dispatch: any, getState) => {
    //dispatch(showLoader(true))
    httpService(getAccessToken(getState()))
        .get(`users/permissions`)
        .then(res => {
            // dispatch(showLoader(false))
            dispatch(setAccessPermissions(res.data))
        })
        .catch(error => {
            appInsights.trackException({
                exception: error,
                properties: {
                    Request: error.request,
                    Response: error.response,
                    message: "Insufficient permission to access the application",
                },
            })

            dispatch(
                setErrorList({
                    traceId: appInsights.context.telemetryTrace.traceID,
                    message: "Insufficient permission to access the application",
                    statusCode: error.response && error.response.status,
                }),
            )
            // dispatch(showLoader(false))
        })
}
